import { useEffect, useState } from "react";
import { ShieldCheck, Upload, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { VerifiedBadges } from "@/components/VerifiedBadges";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";

const TYPES = [["identity", "Identity (Govt. ID)"], ["selfie", "Selfie (owner) verification"], ["education", "Education"], ["employment", "Employment"], ["photo", "Photo"]];
const SCLS = { pending: "text-amber-700 bg-amber-50", approved: "text-emerald-700 bg-emerald-50", rejected: "text-rose-700 bg-rose-50", resubmission: "text-amber-700 bg-amber-50", verified: "text-emerald-700 bg-emerald-50", expiring: "text-amber-700 bg-amber-50", expired: "text-rose-700 bg-rose-50", not_verified: "text-slate-500 bg-slate-50" };

export default function Verification() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/verification").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  if (!data) return <FullSpinner />;

  const submit = async (type) => {
    setBusy(type);
    try { await api.post("/verification", { type, note: "Submitted for review", document_ref: `doc-${type}` }); toast.success("Submitted for review"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="verification-page">
      <div>
        <h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-sky-700" /> ManaJodi Trust</h1>
        <p className="text-sm text-[#605255]">Verification badges appear only after our team confirms your details. They show exactly what has been verified — not a trustworthiness score.</p>
      </div>

      <div className="mj-card p-5">
        <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3">Your Verification Badges</h2>
        <VerifiedBadges verification={data.badges} />
      </div>

      <div className="mj-card p-5">
        <h2 className="font-serif text-lg font-semibold text-[#261A1C] mb-3">Verification Center</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {TYPES.map(([type, label]) => {
            const req = data.requests.find((r) => r.type === type);
            const state = data.states?.[type] || (data.badges?.[type] ? "verified" : "not_verified");
            const exp = data.expiry?.[type];
            const pendingReq = req && req.status === "pending";
            const canSubmit = (state === "not_verified" || state === "expired") && !pendingReq;
            return (
              <div key={type} className="border border-[#E8DFD1] rounded-xl p-4" data-testid={`verif-${type}`}>
                <div className="font-medium text-sm text-[#261A1C] mb-1">{label}</div>
                {pendingReq ? <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full text-amber-700 bg-amber-50">under review</span>
                  : (state === "verified" || state === "expiring") ? (
                    <div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${SCLS[state]}`}>{state === "expiring" ? "Expiring soon" : "Verified"}</span>
                      {exp && <div className="text-[10px] text-[#9E7B1B] mt-1">Valid till {new Date(exp).toLocaleDateString()}</div>}
                    </div>
                  ) : state === "expired" ? (
                    <div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${SCLS.expired}`}>Expired — reverify</span>
                      <button onClick={() => submit(type)} disabled={busy === type} className="btn-outline text-xs mt-2 block">{busy === type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5" /> Reverify</>}</button>
                    </div>
                  ) : canSubmit ? (
                    <button onClick={() => submit(type)} disabled={busy === type} className="btn-outline text-xs mt-1">{busy === type ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5" /> {type === "selfie" ? "Start selfie check" : "Submit"}</>}</button>
                  ) : <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${SCLS[req?.status] || SCLS.not_verified}`}>{req?.status || "not verified"}</span>}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-[#9E7B1B] mt-3">Sensitive documents are stored securely with restricted admin access.</p>
      </div>
    </div>
  );
}
